import React from "react";
import { User, Target, Shield, Award } from "lucide-react";
import { Senior, Roadmap, UserBadges } from "../../types";

interface MyInfoPageProps {
  selectedRoadmap: Roadmap | null;
  followedPaths: number[];
  seniors: Senior[];
  maskName: (name: string) => string;
  setFollowedPaths: (paths: number[]) => void;
  setCurrentPage: (page: string) => void;
  userBadges: UserBadges;
}

const MyInfoPage: React.FC<MyInfoPageProps> = ({
  selectedRoadmap,
  followedPaths,
  seniors,
  maskName,
  setFollowedPaths,
  setCurrentPage,
  userBadges,
}) => (
  <div className="p-6">
    <h2 className="text-2xl font-bold mb-6">내 정보</h2>
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm mb-6">
      <div className="flex items-center mb-4">
        <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center mr-4">
          <User className="w-8 h-8 text-gray-600" />
        </div>
        <div>
          <h3 className="text-xl font-semibold">사용자님</h3>
          <p className="text-gray-600">전북대학교 재학생</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="text-gray-500">관심 분야</p>
          <p className="font-medium">IT/금융</p>
        </div>
        <div>
          <p className="text-gray-500">희망 지역</p>
          <p className="font-medium">전주시</p>
        </div>
      </div>
    </div>

    {/* 내 로드맵 */}
    {selectedRoadmap && (
      <div className="mb-6">
        <h4 className="font-semibold text-lg mb-3 flex items-center">
          <Target className="w-5 h-5 mr-2 text-blue-600" />내 커리어 로드맵
        </h4>
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-xl">
          <h5 className="font-semibold mb-2">{selectedRoadmap.title}</h5>
          <div className="space-y-2">
            {selectedRoadmap.steps.map((step, idx) => (
              <div key={idx} className="flex items-center">
                <div className="w-8 h-8 bg-white border-2 border-blue-500 rounded-full flex items-center justify-center text-sm font-semibold">
                  {idx + 1}
                </div>
                <div className="ml-3 flex-1">
                  <p className="font-medium text-sm">{step.title}</p>
                  <p className="text-xs text-gray-600">
                    {step.year}년 · {step.details}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <button className="mt-3 text-sm text-blue-600 font-medium">
            진행 상황 업데이트 →
          </button>
        </div>
      </div>
    )}

    {/* 따라가는 선배 */}
    <div className="mb-6">
      <h4 className="font-semibold text-lg mb-3">따라가는 선배</h4>
      <div className="bg-gray-50 p-4 rounded-xl">
        {followedPaths.length > 0 ? (
          <div className="space-y-2">
            {followedPaths.map((id) => {
              const senior = seniors.find((s) => s.id === id);
              return (
                <div
                  key={id}
                  className="flex items-center justify-between bg-white p-3 rounded-lg"
                >
                  <div>
                    <span className="font-medium flex items-center">
                      {senior && maskName(senior.name)}
                      <Shield className="w-3 h-3 ml-1 text-gray-400" />
                    </span>
                    <span className="text-sm text-gray-600">
                      {senior && senior.company}
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      if (
                        // eslint-disable-next-line no-restricted-globals
                        confirm(
                          "정말로 이 선배의 경로 따라가기를 취소하시겠습니까?"
                        )
                      ) {
                        setFollowedPaths(
                          followedPaths.filter((pathId) => pathId !== id)
                        );
                      }
                    }}
                    className="text-red-600 text-sm hover:underline"
                  >
                    취소
                  </button>
                </div>
              );
            })}
            <button
              onClick={() => setCurrentPage("feedback")}
              className="w-full mt-3 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium"
            >
              선배와 대화하기
            </button>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">
            아직 따라가는 선배가 없습니다
          </p>
        )}
      </div>
    </div>

    {/* 배지 시스템 */}
    <div className="mb-6">
      <h4 className="font-semibold text-lg mb-3">내 배지</h4>
      
      {/* 획득한 배지 */}
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">획득한 배지</p>
        <div className="grid grid-cols-3 gap-3">
          {userBadges.earned.map((badge, idx) => (
            <div
              key={idx}
              className="bg-yellow-50 p-3 rounded-xl text-center"
            >
              <Award className="w-8 h-8 text-yellow-600 mx-auto mb-1" />
              <p className="text-xs font-semibold text-yellow-800">{badge}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 진행 중인 배지 */}
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">진행 중</p>
        <div className="space-y-2">
          {userBadges.inProgress.map((badge, idx) => (
            <div
              key={idx}
              className="bg-white border border-gray-200 p-3 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">{badge.name}</p>
                <p className="text-xs text-gray-500">
                  {badge.progress}/{badge.total}
                </p>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full"
                  style={{
                    width: `${(badge.progress / badge.total) * 100}%`,
                  }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{badge.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 잠긴 배지 */}
      <div>
        <p className="text-sm text-gray-600 mb-2">미획득 배지</p>
        <div className="grid grid-cols-3 gap-3">
          {userBadges.locked.map((badge, idx) => (
            <div
              key={idx}
              className="bg-gray-100 p-3 rounded-xl text-center opacity-50"
            >
              <Award className="w-8 h-8 text-gray-400 mx-auto mb-1" />
              <p className="text-xs font-semibold text-gray-600">{badge}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default MyInfoPage;